import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    DISCLOSURE_FETCH_ERROR,
    downloadFailureReason,
    logDisclosureDownloadFailed,
    logDisclosureDownloadFinished,
    logDisclosureDownloadStarted,
    playFailureReason,
} from "JS/Helpers/DisclosureAnalytics";
import { captureSentryError } from "JS/Helpers/SentryHelper";
import { messaging } from "JS/Helpers/UserMessaging";
import { useAppSelector, useAppDispatch } from "JS/Redux/Store";
import {
    DisclosurePrefetch,
    clearDisclosurePrefetch,
    recordDisclosureBackfilled,
    setDisclosureCatalogue,
    setDisclosurePlaying,
    setDisclosurePrefetch,
} from "JS/Redux/Disclosure";
import { DisclosureService } from "JS/Services/Disclosure";
import { mediaService } from "JS/Services/Media";
import {
    Disclosure,
    DisclosureCatalogue,
    DisclosureClip,
    DisclosurePlacement,
    DisclosurePlayContext,
    DisclosurePlayReason,
    catalogueClips,
    clipId,
    describeClip,
    hasClip,
    disclosureFingerprint,
    requiresDisclosure,
} from "JS/Models/Disclosure";
import { ContentType, contentTypeOf } from "JS/Models/Media";
import { getDexieConnection } from "JS/Database/Dexie";
import { useDisclosureAnalytics } from "./DisclosureAnalytics";
import { useOwnedBlobUrl } from "./Media";
import {
    catalogueClipIds,
    deleteSupersededClips,
    getClipBlob,
    getStoredClipIds,
    putDisclosureClip,
} from "JS/Database/Disclosure";
import { useGlobalMediaEssentials } from "./MediaEssentials";
import { config } from "JS/Config";

export const service = new DisclosureService();

export const useGlobalDisclosure = () => {
    const dispatch = useAppDispatch();
    const { catalogue, fingerprint } = useAppSelector(
        (state) => state.disclosure,
    );

    return {
        catalogue,
        fingerprint,
        setCatalogue: (next: DisclosureCatalogue) =>
            dispatch(
                setDisclosureCatalogue({
                    catalogue: next,
                    fingerprint: disclosureFingerprint(next),
                }),
            ),
    };
};

export const useDisclosurePlaying = () =>
    useAppSelector((state) => state.disclosure?.playing === true);

export const usePublishDisclosurePlaying = (isDisclosure: boolean) => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setDisclosurePlaying(isDisclosure));

        return () => {
            dispatch(setDisclosurePlaying(false));
        };
    }, [dispatch, isDisclosure]);
};

// The feature switch. Off means the app behaves exactly as it did before disclosures.
export const useDisclosureEnabled = () => {
    const { mediaEssentials } = useGlobalMediaEssentials();

    return mediaEssentials?.allow_disclosure_feature === true;
};

// the S3 text when it is usable, else the built-in one
export const useDisclosureUnavailableMessage = () => {
    const { mediaEssentials } = useGlobalMediaEssentials();
    const fromS3 = mediaEssentials?.disclosure_unavailable_msg;

    return typeof fromS3 === "string" && fromS3.trim().length > 0
        ? fromS3
        : messaging.disclosure.unavailableOnline;
};

export const useDisclosureCatalogue = () => {
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const enabled = useDisclosureEnabled();
    const { catalogue, fingerprint, setCatalogue } = useGlobalDisclosure();

    const refetch = useCallback(async () => {
        if (!enabled) return undefined;

        setLoading(true);
        try {
            const response = await service.getDisclosures();
            const next = response?.data;
            if (!next) return undefined;

            if (disclosureFingerprint(next) !== fingerprint) setCatalogue(next);

            setFailed(false);
            return next;
        } catch (error) {
            captureSentryError(error as Error, {
                location: "Disclosure",
                stage: "catalogue",
            });
            setFailed(true);
            return undefined;
        } finally {
            setLoading(false);
        }
    }, [enabled, fingerprint, setCatalogue]);

    return {
        loading,
        failed,
        enabled,
        catalogue,
        fingerprint,
        refetch,
    };
};

let catalogueChecked = false;
let backfillChecked = false;

export const resetDisclosureSession = () => {
    catalogueChecked = false;
    backfillChecked = false;
    failedClips.clear();
};

export const useDisclosureLaunchCheck = () => {
    const { enabled, refetch } = useDisclosureCatalogue();
    const dispatch = useAppDispatch();
    const backfill = useDisclosureBackfill();

    const latest = useRef(refetch);
    latest.current = refetch;
    const latestBackfill = useRef(backfill);
    latestBackfill.current = backfill;

    const report = useCallback(
        (progress: DisclosurePrefetch) => {
            if (progress.completed >= progress.total) {
                dispatch(clearDisclosurePrefetch());
                return;
            }
            dispatch(setDisclosurePrefetch(progress));
        },
        [dispatch],
    );

    const check = useCallback(() => {
        return latest
            .current()
            .then((next) => storeDisclosureClips(next, report))
            // strictly after the clip prefetch, so the two never compete
            .then(() => latestBackfill.current())
            .catch((error) => {
                dispatch(clearDisclosurePrefetch());
                captureSentryError(error as Error, {
                    location: "Disclosure",
                    stage: "store",
                });
            });
    }, [dispatch, report]);

    useEffect(() => {
        if (!enabled) return;

        if (!catalogueChecked) {
            catalogueChecked = true;
            // progress is persisted with the catalogue, so a prefetch cut short
            // by a close would otherwise show again on the next launch
            dispatch(clearDisclosurePrefetch());
            check();
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") check();
        };

        document.addEventListener("visibilitychange", onVisibilityChange);

        return () =>
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
    }, [enabled, check, dispatch]);
};

const isOnline = () => globalThis.navigator?.onLine !== false;

const signClip = async (clip: DisclosureClip) => {
    const signed = await mediaService.getSignedUrl(
        clip.bucket_name,
        clip.postfix,
        clip.file_name,
    );

    return signed?.media_url;
};

const networkUrl = async (clip: DisclosureClip) =>
    clip.url || (await signClip(clip));

const storeClipFromNetwork = async (
    clip: DisclosureClip,
    catalogue?: DisclosureCatalogue,
) => {
    const id = clipId(clip);
    if (!id || !isOnline()) return false;

    const url = await networkUrl(clip);
    if (!url) return false;

    const response = await fetch(url);
    if (!response.ok) {
        const failed = new Error(
            `Disclosure clip fetch failed: ${response.status}`,
        );
        failed.name = DISCLOSURE_FETCH_ERROR;
        throw failed;
    }

    await putDisclosureClip({ id, blob: await response.blob() });

    if (catalogue && isOnline())
        await deleteSupersededClips([...catalogueClipIds(catalogue), id]);

    return true;
};

export const cacheDisclosureClip = async (
    clip: DisclosureClip,
    catalogue?: DisclosureCatalogue,
    skuId?: string,
) => {
    const id = clipId(clip);
    if (!id || !isOnline()) return false;

    const context = {
        disclosureId: id,
        ...describeClip(clip, catalogue),
        skuId,
    };

    logDisclosureDownloadStarted(context);

    try {
        const stored = await storeClipFromNetwork(clip, catalogue);

        if (stored) logDisclosureDownloadFinished(context);
        else logDisclosureDownloadFailed(context, "signedUrlInvalid");

        return stored;
    } catch (error) {
        logDisclosureDownloadFailed(context, downloadFailureReason(error));
        throw error;
    }
};

const failedClips = new Set<string>();

export type DisclosurePrefetchProgress = (progress: DisclosurePrefetch) => void;

export const storeDisclosureClips = async (
    catalogue?: DisclosureCatalogue,
    onProgress?: DisclosurePrefetchProgress,
) => {
    if (!catalogue) return 0;

    const clips = catalogueClips(catalogue);
    const stored = await getStoredClipIds();
    const missing = clips.filter(
        (clip) =>
            !stored.includes(clipId(clip)) && !failedClips.has(clipId(clip)),
    );

    const total = missing.length;
    let completed = 0;
    const report = (fileName?: string) =>
        onProgress?.({ total, completed, fileName });

    report();

    for (const clip of missing) {
        report(clip.file_name);

        try {
            await cacheDisclosureClip(clip, catalogue);
        } catch (error) {
            failedClips.add(clipId(clip));
            captureSentryError(error as Error, {
                location: "Disclosure",
                stage: "prefetch",
                fileName: clip.file_name,
            });
        }

        // a clip we gave up on is still one fewer left to wait for
        completed += 1;
        report(clip.file_name);
    }

    await deleteSupersededClips(clips.map(clipId));

    return total;
};

interface ResolvedClip {
    url?: string;
    reason?: DisclosurePlayReason;
}

const resolveClipUrl = async (
    clip: DisclosureClip,
    catalogue?: DisclosureCatalogue,
    skuId?: string,
): Promise<ResolvedClip> => {
    let blob: Blob | undefined;
    let storageFailed = false;

    try {
        blob = await getClipBlob(clip);
    } catch {
        storageFailed = true;
    }

    if (blob) return { url: URL.createObjectURL(blob) };

    if (!isOnline())
        return { reason: storageFailed ? "storageFailed" : "offlineNotCached" };

    let streamed: string | undefined;

    try {
        streamed = await networkUrl(clip);
    } catch (error) {
        return { reason: playFailureReason(error) };
    }

    if (!streamed) return { reason: "signedUrlInvalid" };

    cacheDisclosureClip(clip, catalogue, skuId).catch((error) =>
        captureSentryError(error as Error, {
            location: "Disclosure",
            stage: "backfill",
            fileName: clip.file_name,
        }),
    );

    return { url: streamed };
};

export const useDisclosureClipUrl = (clip?: DisclosureClip, skuId?: string) => {
    const { catalogue } = useGlobalDisclosure();
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [reason, setReason] = useState<DisclosurePlayReason | undefined>(
        undefined,
    );
    const [settledId, setSettledId] = useState("");

    const id = clipId(clip);

    // derived, not seeded at mount: a scene renders before its detail response
    // lands, so a clip that arrives later must still read as resolving
    const resolving = !!id && settledId !== id;

    useEffect(() => {
        if (!clip || !id) {
            setUrl(undefined);
            setReason(undefined);
            setSettledId("");
            return;
        }

        let live = true;

        resolveClipUrl(clip, catalogue, skuId)
            .then((next) => {
                if (!live) return;
                setUrl(next.url);
                setReason(next.reason);
            })
            .catch(() => {
                if (live) setUrl(undefined);
            })
            .finally(() => {
                if (live) setSettledId(id);
            });

        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useOwnedBlobUrl(url);

    return { url, resolving, reason };
};

export interface DisclosureGateOptions {
    disclosure?: Disclosure;
    skuId?: string;
    nId?: string;
    contentType?: ContentType;
}

export const useDisclosureGate = (options: DisclosureGateOptions) => {
    const { disclosure, skuId, nId, contentType } = options;

    const enabled = useDisclosureEnabled();
    const unavailableOnline = useDisclosureUnavailableMessage();
    const reported = useRef(false);
    const failuresReported = useRef(new Set<string>());
    const { recordPlayFailed } = useDisclosureAnalytics();

    const required = enabled && requiresDisclosure(disclosure);
    const start = useDisclosureClipUrl(
        required ? disclosure?.start : undefined,
        skuId,
    );
    const end = useDisclosureClipUrl(
        required ? disclosure?.end : undefined,
        skuId,
    );

    const resolving = start.resolving || end.resolving;
    const missing =
        required &&
        !resolving &&
        ((hasClip(disclosure?.start) && !start.url) ||
            (hasClip(disclosure?.end) && !end.url));

    const analytics = useMemo<DisclosurePlayContext>(
        () => ({ skuId, nId, contentType }),
        [skuId, nId, contentType],
    );

    const startId = clipId(disclosure?.start);
    const endId = clipId(disclosure?.end);

    useEffect(() => {
        if (!missing || reported.current) return;

        reported.current = true;
        captureSentryError(new Error("Disclosure media unavailable"), {
            location: "Disclosure",
            stage: "playback",
            skuId: skuId ?? "unknown",
            startFile: disclosure?.start?.file_name ?? "none",
            endFile: disclosure?.end?.file_name ?? "none",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missing, skuId]);

    useEffect(() => {
        if (!required || resolving) return;

        const sides: [DisclosurePlacement, string, ResolvedClip][] = [
            ["start", startId, { url: start.url, reason: start.reason }],
            ["end", endId, { url: end.url, reason: end.reason }],
        ];

        sides.forEach(([placement, disclosureId, side]) => {
            // a reason only exists once the clip has finished resolving
            if (!disclosureId || side.url || !side.reason) return;

            const key = `${placement}|${disclosureId}`;
            if (failuresReported.current.has(key)) return;
            failuresReported.current.add(key);

            recordPlayFailed({
                ...analytics,
                disclosureId,
                placement,
                reason: side.reason,
            });
        });
    }, [
        required,
        resolving,
        startId,
        endId,
        start.url,
        start.reason,
        end.url,
        end.reason,
        analytics,
        recordPlayFailed,
    ]);

    return {
        startUrl: start.url,
        endUrl: end.url,
        required,
        resolving,
        blocked: missing,
        title: isOnline()
            ? messaging.disclosure.unavailableOnlineTitle
            : messaging.disclosure.unavailableOfflineTitle,
        message: isOnline()
            ? unavailableOnline
            : messaging.disclosure.unavailableOffline,
        analytics,
    };
};

const BACKFILL_CHUNK = 3;

interface BackfillTarget {
    nid: string;
    ids: string[];
    isVideo: boolean;
}

// a row id is `${memberId}-${nid}` or `${memberId}-${nid}-${position}`
const rowNid = (id: string) => {
    const row = `${id}`;
    const prefix = `${config.user.memberId}-`;

    // the table holds every member on the device, so never drop a row that will not prefix-match
    return row.startsWith(prefix)
        ? (row.slice(prefix.length).split("-")[0] ?? "")
        : (row.split("-")[1] ?? "");
};

const groupRowsByNid = (
    ids: string[],
    isVideo: boolean,
    into: Map<string, BackfillTarget>,
) => {
    ids.forEach((id) => {
        const nid = rowNid(id);
        if (!nid) return;

        const key = `${isVideo ? "video" : "audio"}|${nid}`;
        const target = into.get(key) ?? { nid, ids: [], isVideo };
        target.ids.push(id);
        into.set(key, target);
    });
};

// downloads made before this shipped carry no descriptor; learn it once, online
export const useDisclosureBackfill = () => {
    const enabled = useDisclosureEnabled();
    const dispatch = useAppDispatch();
    const backfilled = useAppSelector((state) => state.disclosure?.backfilled);
    const gifts = useAppSelector((state) => state.gifts);

    const doneRef = useRef(backfilled);
    doneRef.current = backfilled;
    const giftsRef = useRef(gifts);
    giftsRef.current = gifts;

    // a stored row does not record whether it was a gift; the cached listings do
    const detailType = useCallback((nid: string, isVideo: boolean) => {
        const state = giftsRef.current;
        const received = [
            ...(state?.receivedMemberAudios ?? []),
            ...(state?.receivedProspectAudios ?? []),
            ...(state?.receivedMemberVideos ?? []),
            ...(state?.receivedProspectVideos ?? []),
        ].find((item) => item?.nid === nid);

        return contentTypeOf(received, isVideo ? "Video" : "MP3");
    }, []);

    const learn = useCallback(
        async (target: BackfillTarget) => {
            try {
                const detail = await mediaService.getContentDetails(
                    target.nid,
                    detailType(target.nid, target.isVideo),
                );
                const disclosure = detail?.data?.disclosure;
                const db = getDexieConnection();

                if (requiresDisclosure(disclosure))
                    await Promise.all(
                        target.ids.map((id) =>
                            target.isVideo
                                ? db.videos.update(id, { disclosure })
                                : db.audios.update(id, { disclosure }),
                        ),
                    );

                return target.nid;
            } catch (error) {
                captureSentryError(error as Error, {
                    location: "Disclosure",
                    stage: "downloadBackfill",
                    nid: target.nid,
                });
                return undefined;
            }
        },
        [detailType],
    );

    return useCallback(async () => {
        if (!enabled || backfillChecked || !isOnline()) return;
        backfillChecked = true;

        try {
            const db = getDexieConnection();
            const targets = new Map<string, BackfillTarget>();

            groupRowsByNid(
                (await db.audios.toCollection().primaryKeys()).map(String),
                false,
                targets,
            );
            groupRowsByNid(
                (await db.videos.toCollection().primaryKeys()).map(String),
                true,
                targets,
            );

            const done = doneRef.current ?? [];
            const pending = Array.from(targets.values()).filter(
                (target) => !done.includes(target.nid),
            );

            // small chunks so the member's own requests are not starved
            for (let i = 0; i < pending.length; i += BACKFILL_CHUNK) {
                const learned = await Promise.all(
                    pending.slice(i, i + BACKFILL_CHUNK).map(learn),
                );
                const resolved = learned.filter(Boolean) as string[];

                if (resolved.length)
                    dispatch(recordDisclosureBackfilled(resolved));
            }
        } catch (error) {
            captureSentryError(error as Error, {
                location: "Disclosure",
                stage: "downloadBackfill",
            });
        }
    }, [enabled, dispatch, learn]);
};
